/**
 * Utility functions for data processing and transformation
 */

/**
 * Get shape of multi-dimensional array
 */
export function getArrayShape(arr: any): number[] {
  if (typeof arr === 'number') return [];
  if (!Array.isArray(arr)) return [];

  const shape = [arr.length];
  if (arr.length > 0 && Array.isArray(arr[0])) {
    shape.push(...getArrayShape(arr[0]));
  }
  return shape;
}

/**
 * Download blob as file
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Compute a sort permutation that sorts the given labels alphabetically.
 * Returns an array of original indices in alphabetical order.
 *
 * Example: labels = ['ZAF', 'AUS', 'BEL']
 *   returns [1, 2, 0]  (AUS at 1, BEL at 2, ZAF at 0)
 */
export function alphabeticalPermutation(labels: string[]): number[] {
  return labels
    .map((label, idx) => ({ label, idx }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .map(item => item.idx);
}

/**
 * Reorder a 1D array by the given permutation of indices.
 */
export function permuteArray1D(arr: number[], perm: number[]): number[] {
  return perm.map(i => arr[i]);
}

/**
 * Reorder rows of a 2D array by the given permutation (applies to the first axis).
 */
export function permuteRows2D(arr: number[][], perm: number[]): number[][] {
  return perm.map(i => arr[i]);
}

/**
 * Reorder columns of a 2D array by the given permutation (applies to the second axis).
 */
export function permuteCols2D(arr: number[][], perm: number[]): number[][] {
  return arr.map(row => perm.map(j => row[j]));
}

/**
 * Safe element-wise CF/baseline ratio: cf/baseline - 1.
 *
 * When both values are near zero (|base| < 1e-15 and |v| < 1e-15), returns 0
 * instead of -1 (the artifact from the 1e-30 epsilon guard when both are zero).
 *
 * Shared between VariableVisualization and ScenarioVisualization (Fix 6).
 */
export function safeRatio(v: number, base: number): number {
  if (Math.abs(base) < 1e-15 && Math.abs(v) < 1e-15) return 0;
  return v / (base + 1e-30) - 1;
}
